import dayjs from "dayjs";
import state from "@/store/state";

function getSimultaneousTimeSlot(timeSlot) {
  let timeSlotStart = dayjs(
    timeSlot.date + "T" + timeSlot.time,
    "YYYY-MM-DD[T]HH:mm"
  );
  let timeSlotEnd = dayjs(timeSlotStart).add(timeSlot.duration, "m");

  let simultaneousTimeSlot = [];
  state.timeSlots.forEach((ts) => {
    if (timeSlot.id === ts.id) {
      return;
    }

    if (timeSlot.date !== ts.date) {
      return;
    }

    let itemStart = dayjs(ts.date + "T" + ts.time, "YYYY-MM-DD[T]HH:mm");
    let itemEnd = dayjs(itemStart).add(ts.duration, "m");

    if (
      itemStart.isAfter(timeSlotEnd) ||
      itemStart.isSame(timeSlotEnd) ||
      itemEnd.isBefore(timeSlotStart) ||
      itemEnd.isSame(timeSlotStart)
    ) {
      return;
    }

    simultaneousTimeSlot.push(ts);
  });

  return simultaneousTimeSlot;
}

/**
 * Check if events are compatible (get happen in the same time)
 */
function canBeSimultaneous(event1, event2) {
  let otherEvents = [event1, event2].filter(
    (event) => event.category !== "service"
  );

  if (otherEvents.length === 1) {
    return state.eventTypes[otherEvents.pop().subtype].with_service;
  }

  if (otherEvents.length === 2) {
    return otherEvents.every(
      (event) => state.eventTypes[event.subtype].with_other
    );
  }

  // Event are services : should belong to at least one eventTypesGroup
  return (
    state.eventTypesGroup.filter((group) => {
      if (group.eventTypes.includes(event1.subtype)) {
        return group.eventTypes.includes(event2.subtype);
      }

      return false;
    }).length !== 0
  );
}

function canBePositionable(timeSlot) {
  if (!state.customer["has_old_week_freeze"] || state.impersonate?.admin) {
    return true;
  }

  const semMin = dayjs()
    .subtract(2, "w")
    .day(1)
    .hour(0)
    .minute(0)
    .second(0)
    .millisecond(0);
  const txtDate = Array.isArray(timeSlot.date)
    ? timeSlot.date[0]
    : timeSlot.date;
  const date = dayjs(dayjs(txtDate).day(1));

  return date.isAfter(semMin) || date.isSame(semMin);
}

function isInsideWorkContract(timeSlot) {
  if (!state.customer || !timeSlot) {
    return true;
  }

  // Do not restrict to admin users
  if (
    state.customer?.roles?.includes("ROLE_ADMIN") ||
    state.impersonate?.admin
  ) {
    return true;
  }

  const startAt = state.customer.hasOwnProperty("start_at")
    ? dayjs(state.customer.start_at).startOf("day")
    : null;
  const expireAt = state.customer.hasOwnProperty("expire_at")
    ? dayjs(state.customer.expire_at).endOf("day")
    : null;
  const timeSlotStart = Array.isArray(timeSlot.date)
    ? dayjs(`${timeSlot.date[0]} ${timeSlot.time}`)
    : dayjs(`${timeSlot.date} ${timeSlot.time}`);
  const timeSlotEnd = Array.isArray(timeSlot.date)
    ? dayjs(`${timeSlot.date[timeSlot.date.length - 1]} ${timeSlot.time}`).add(
        timeSlot.duration,
        "minute"
      )
    : dayjs(`${timeSlot.date} ${timeSlot.time}`).add(
        timeSlot.duration,
        "minute"
      );

  return !(
    (!!startAt &&
      startAt.isValid() &&
      timeSlotStart.isValid() &&
      timeSlotStart.toDate() < startAt.toDate()) ||
    (!!expireAt &&
      expireAt.isValid() &&
      timeSlotEnd.isValid() &&
      timeSlotEnd.toDate() > expireAt.toDate())
  );
}

function endAfterMidnight(timeSlot) {
  const tsStart = dayjs(`${timeSlot.date}T${timeSlot.time}`);
  const tsEnd = tsStart.clone().add(timeSlot.duration, "m");

  return tsStart.date() !== tsEnd.date();
}

function getStartEndTimeSlot(timeSlot) {
  let timeSlotStart = dayjs(
    timeSlot.date + "T" + timeSlot.time,
    "YYYY-MM-DD[T]HH:mm"
  );
  let timeSlotEnd = dayjs(timeSlotStart).add(timeSlot.duration, "m");

  let res =
    dayjs(timeSlotStart).format("H[h]mm") +
    " - " +
    dayjs(timeSlotEnd).format("H[h]mm");

  if (timeSlot.break_duration) {
    res += " (pause: " + timeSlot.break_duration + " min)";
  }

  return res;
}

function getStartEndCalendarEvent(event) {
  return `${dayjs(event.start).format("H[h]mm")} - ${dayjs(event.end).format(
    "H[h]mm"
  )}`;
}

function needLevel(event) {
  if (!event) {
    return false;
  }

  const isLevelRequired = state.eventTypes[event.subtype].require_level1;
  const isImpersonate = state.impersonate;
  const isAdminOrGestion1 =
    state.customer.roles.includes("ROLE_ADMIN") ||
    state.customer.roles.includes("ROLE_GESTION_1");

  return !!(
    isLevelRequired &&
    isImpersonate === undefined &&
    !isAdminOrGestion1
  );
}

const eventHasPermission = (event, route) => {
  return route.name !== "auditor-calendar" && !event?.suspended;
};

const eventIsConfirmedOrPlanned = (event) => {
  return event?.state === "planned" || event?.state === "confirmed";
};

/**
 * @param owner
 * @param eventType
 * @returns {boolean}
 */
function eventIsPT(owner, eventType) {
  const linkedCredit = eventType?.linked_credit;
  const isOwnerAndEventTypePT =
    owner?.main_folder?.code === "pt" &&
    !!eventType?.foldersCode?.includes("pt");

  if (linkedCredit) {
    return isOwnerAndEventTypePT && !!linkedCredit?.foldersCode?.includes("pt");
  }

  return isOwnerAndEventTypePT;
}

/**
 * @param owner
 * @param eventType
 * @returns {boolean}
 */
function eventCanBePlacedOnDifferentYear(owner, eventType) {
  return eventType?.category === "service" || eventIsPT(owner, eventType);
}

export {
  getSimultaneousTimeSlot,
  canBeSimultaneous,
  canBePositionable,
  isInsideWorkContract,
  endAfterMidnight,
  getStartEndTimeSlot,
  needLevel,
  getStartEndCalendarEvent,
  eventHasPermission,
  eventIsConfirmedOrPlanned,
  eventIsPT,
  eventCanBePlacedOnDifferentYear,
};
